import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import { Dots, Public } from "../../svg";
import CreateComment from "./CreateComment";
import Comment from "./Comment";
import ReactsPopup from "./ReactsPopup";
import ReactionsModal from "./ReactionsModal";
import { getReacts, reactPost } from "../../functions/post";
import "./postModal.css";

export default function PostModal({ post, user, setShowModal, onShare }) {
  const modalRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [reacts, setReacts] = useState([]);
  const [check, setCheck] = useState();
  const [total, setTotal] = useState(0);
  const [comments, setComments] = useState([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [selectedReactType, setSelectedReactType] = useState(null);

  useEffect(() => {
    getPostReacts();
    setComments(post?.comments || []);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  const getPostReacts = async () => {
    const res = await getReacts(post._id, user.token);
    setReacts(res.reacts || []);
    setCheck(res.check);
    setTotal(res.total || 0);
  };

  const reactHandler = async (type) => {
    reactPost(post._id, type, user.token);
    if (check === type) {
      setCheck();
      let index = reacts.findIndex((x) => x.react === check);
      if (index !== -1) {
        const newReacts = [...reacts];
        newReacts[index].count = Math.max(0, newReacts[index].count - 1);
        setReacts(newReacts);
        setTotal((prev) => Math.max(0, prev - 1));
      }
    } else {
      const oldCheck = check;
      setCheck(type);
      let index = reacts.findIndex((x) => x.react === type);
      let index1 = reacts.findIndex((x) => x.react === oldCheck);
      const newReacts = [...reacts];
      if (index !== -1) {
        newReacts[index].count = (newReacts[index].count || 0) + 1;
        setTotal((prev) => prev + 1);
      }
      if (index1 !== -1 && oldCheck) {
        newReacts[index1].count = Math.max(0, newReacts[index1].count - 1);
        setTotal((prev) => Math.max(0, prev - 1));
      }
      setReacts(newReacts);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowModal(false);
    } else if (e.key === "ArrowLeft" && post.images?.length > 1) {
      setCurrentMediaIndex((prev) =>
        prev === 0 ? post.images.length - 1 : prev - 1
      );
    } else if (e.key === "ArrowRight" && post.images?.length > 1) {
      setCurrentMediaIndex((prev) =>
        prev === post.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.images?.length]);

  const isVideo = (url) => {
    return url?.match(/\.(mp4|webm|ogg|mov)$/i) || url?.includes("video");
  };

  const openReactionsModal = (reactType = null) => {
    setSelectedReactType(reactType);
    setShowReactionsModal(true);
  };

  const hasMedia = post.images && post.images.length > 0;
  const hasBackground = post.background;

  return (
    <div className="post_modal_overlay" onClick={handleBackdropClick}>
      {showReactionsModal && (
        <ReactionsModal
          postId={post._id}
          token={user.token}
          setShowModal={setShowReactionsModal}
          selectedType={selectedReactType}
          reacts={reacts}
          total={total}
        />
      )}
      <div
        className={`post_modal ${hasMedia ? "with_media" : "no_media"}`}
        ref={modalRef}
      >
        <button
          className="post_modal_close"
          onClick={() => setShowModal(false)}
        >
          <i className="exit_icon"></i>
        </button>

        {/* Media Section */}
        {hasMedia && (
          <div className="post_modal_media">
            {post.images.length > 1 && (
              <>
                <button
                  className="media_nav_btn prev"
                  onClick={() =>
                    setCurrentMediaIndex((prev) =>
                      prev === 0 ? post.images.length - 1 : prev - 1
                    )
                  }
                >
                  <i className="arrow_back_icon"></i>
                </button>
                <button
                  className="media_nav_btn next"
                  onClick={() =>
                    setCurrentMediaIndex((prev) =>
                      prev === post.images.length - 1 ? 0 : prev + 1
                    )
                  }
                >
                  <i className="arrow_back_icon" style={{ transform: "rotate(180deg)" }}></i>
                </button>
              </>
            )}
            <div className="media_container">
              {isVideo(post.images[currentMediaIndex]?.url) ? (
                <video
                  src={post.images[currentMediaIndex]?.url}
                  controls
                  autoPlay
                  className="modal_video"
                />
              ) : (
                <img
                  src={post.images[currentMediaIndex]?.url}
                  alt=""
                  className="modal_image"
                />
              )}
            </div>
            {post.images.length > 1 && (
              <div className="media_indicators">
                {post.images.map((_, i) => (
                  <span
                    key={i}
                    className={`indicator ${i === currentMediaIndex ? "active" : ""}`}
                    onClick={() => setCurrentMediaIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Background Post */}
        {hasBackground && !hasMedia && (
          <div
            className="post_modal_background"
            style={{ backgroundImage: `url(${post.background})` }}
          >
            <div className="background_text">{post.text}</div>
          </div>
        )}

        {/* Content Section */}
        <div className="post_modal_content">
          {/* Header */}
          <div className="modal_header">
            <Link
              to={`/profile/${post.user.username}`}
              className="modal_header_left"
            >
              <img src={post.user.picture} alt="" className="modal_user_img" />
              <div className="modal_header_col">
                <span className="modal_user_name">
                  {post.user.first_name} {post.user.last_name}
                </span>
                <span className="modal_post_date">
                  <Moment fromNow>{post.createdAt}</Moment> · <Public color="#828387" />
                </span>
              </div>
            </Link>
            <div className="modal_header_right">
              <div className="small_circle hover1">
                <Dots color="#828387" />
              </div>
            </div>
          </div>

          {/* Text */}
          {post.text && !hasBackground && (
            <div className="modal_text">{post.text}</div>
          )}

          {/* Reactions Info */}
          <div className="modal_reactions_info">
            <div
              className="reactions_summary"
              onClick={() => openReactionsModal(null)}
            >
              <div className="reactions_icons">
                {reacts &&
                  reacts
                    .filter((r) => r.count > 0)
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 3)
                    .map((react, i) => (
                      <img
                        src={`../../../reacts/${react.react}.svg`}
                        alt={react.react}
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation();
                          openReactionsModal(react.react);
                        }}
                      />
                    ))}
              </div>
              {total > 0 && <span className="reactions_count">{total}</span>}
            </div>
            <div className="comments_shares_count">
              <span>{comments.length} comments</span>
              <span>{post.shares?.length || 0} shares</span>
            </div>
          </div>

          {/* Actions */}
          <div className="modal_actions">
            <ReactsPopup
              visible={visible}
              setVisible={setVisible}
              reactHandler={reactHandler}
            />
            <div
              className="modal_action hover1"
              onMouseOver={() => setTimeout(() => setVisible(true), 500)}
              onMouseLeave={() => setTimeout(() => setVisible(false), 500)}
              onClick={() => reactHandler(check ? check : "like")}
            >
              {check ? (
                <img
                  src={`../../../reacts/${check}.svg`}
                  alt=""
                  style={{ width: "18px" }}
                />
              ) : (
                <i className="like_icon"></i>
              )}
              <span
                style={{
                  color:
                    check === "like"
                      ? "#4267b2"
                      : check === "love"
                      ? "#f63459"
                      : check === "haha" || check === "sad" || check === "wow"
                      ? "#f7b125"
                      : check === "angry"
                      ? "#e4605a"
                      : "",
                }}
              >
                {check ? check.charAt(0).toUpperCase() + check.slice(1) : "Like"}
              </span>
            </div>
            <div className="modal_action hover1">
              <i className="comment_icon"></i>
              <span>Comment</span>
            </div>
            <div className="modal_action hover1">
              <i className="share_icon"></i>
              <span>Share</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="modal_comments_section">
            <CreateComment
              user={user}
              postId={post._id}
              setComments={setComments}
            />
            <div className="modal_comments_list">
              {comments &&
                comments
                  .sort((a, b) => {
                    if (a.isPinned && !b.isPinned) return -1;
                    if (!a.isPinned && b.isPinned) return 1;
                    return new Date(b.commentAt) - new Date(a.commentAt);
                  })
                  .map((comment, i) => (
                    <Comment
                      key={comment._id || i}
                      comment={comment}
                      postId={post._id}
                      user={user}
                      setComments={setComments}
                      isPostOwner={post.user._id === user.id}
                    />
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
